import React from "react";
import { Knob, Fader, Meter, dbLabel } from "./widgets";
import { INSTRUMENTS } from "./engine";

function Strip({ t, ti, mutate, selected, onSelect }) {
  const set = (k, v) => mutate((d) => { d.tracks[ti][k] = v; });
  const setEq = (k, v) => mutate((d) => { d.tracks[ti].eq = { ...d.tracks[ti].eq, [k]: v }; });
  const inst = INSTRUMENTS.find((i) => i.id === t.instrument);
  return (
    <div onClick={onSelect} data-testid={`mixer-strip-${ti}`} className={`w-[92px] shrink-0 flex flex-col items-center border-r border-black py-2 gap-2 ${selected ? "bg-[#393c43]" : "daw-panel"}`}>
      <div className="w-[84px] text-[9px] font-mono text-center text-zinc-400 truncate bg-black/40 rounded px-1 py-0.5" title={inst?.name || "Audio Track"}>{inst?.name || "Audio Track"}</div>
      <div className="w-[84px] rounded bg-black/30 p-1">
        <div className="flex items-center justify-between text-[9px] font-mono text-zinc-500 mb-1 px-0.5">
          <span>PRO EQ</span>
          <button onClick={(e) => { e.stopPropagation(); set("eqOn", t.eqOn === false); }} className={`w-2.5 h-2.5 rounded-full ${t.eqOn !== false ? "bg-sky-400" : "bg-zinc-600"}`} title="Bypass" />
        </div>
        <div className="flex justify-between">
          {["low", "mid", "high"].map((k) => <Knob key={k} size={24} min={-15} max={15} value={t.eq?.[k] || 0} onChange={(v) => setEq(k, v)} label={k[0]} color="#38bdf8" fmt={(v) => v.toFixed(1) + " dB"} />)}
        </div>
      </div>
      <div className="flex gap-1">
        <Knob size={26} min={0} max={1} def={0} value={t.sendRev || 0} onChange={(v) => set("sendRev", v)} label="Rvb" color="#a78bfa" />
        <Knob size={26} min={0} max={1} def={0} value={t.sendDly || 0} onChange={(v) => set("sendDly", v)} label="Dly" color="#34d399" />
      </div>
      <Knob size={32} value={t.pan} onChange={(v) => set("pan", Math.abs(v) < 0.04 ? 0 : v)} label={t.pan === 0 ? "<C>" : t.pan < 0 ? `${Math.round(-t.pan * 100)}L` : `${Math.round(t.pan * 100)}R`} testid={`pan-knob-${ti}`} />
      <div className="flex gap-1">
        <button onClick={(e) => { e.stopPropagation(); set("mute", !t.mute); }} className={`w-7 h-5 rounded-sm text-[10px] font-bold border border-black ${t.mute ? "bg-amber-400 text-black" : "bg-[#1d1f23] text-zinc-400"}`}>M</button>
        <button onClick={(e) => { e.stopPropagation(); set("solo", !t.solo); }} className={`w-7 h-5 rounded-sm text-[10px] font-bold border border-black ${t.solo ? "bg-yellow-300 text-black" : "bg-[#1d1f23] text-zinc-400"}`}>S</button>
      </div>
      <div className="flex gap-2 items-end">
        <Fader value={t.volume} onChange={(v) => set("volume", v)} height={150} testid={`fader-${ti}`} />
        <Meter trackId={t.id} height={150} width={5} />
      </div>
      <div className="text-[10px] font-mono text-zinc-300 bg-black/50 rounded px-1.5">{dbLabel(t.volume)}</div>
      <div className="w-[84px] h-6 rounded-sm flex items-center justify-center text-[11px] font-medium text-black truncate px-1" style={{ background: t.color }}>{t.name}</div>
    </div>
  );
}

export default function Mixer({ song, mutate, sel, setSel }) {
  return (
    <div className="flex-1 flex overflow-x-auto thin-scroll bg-[#17181b] min-h-0" data-testid="mixer-view">
      {song.tracks.map((t, ti) => <Strip key={t.id} t={t} ti={ti} mutate={mutate} selected={sel.trackId === t.id} onSelect={() => setSel({ trackId: t.id, clipId: null })} />)}
      <div className="flex-1" />
      {[{ n: "FX1 Room Reverb", c: "#a78bfa" }, { n: "FX2 Analog Delay", c: "#34d399" }].map((f) => (
        <div key={f.n} className="w-[80px] shrink-0 daw-panel border-l border-black flex flex-col items-center justify-end py-2 gap-2">
          <div className="text-[9px] font-mono text-zinc-500 text-center px-1">RETURN</div>
          <div className="h-[150px] w-2 rounded bg-black/60" />
          <div className="w-[72px] h-6 rounded-sm text-[10px] flex items-center justify-center text-black" style={{ background: f.c }}>{f.n.split(" ")[0]}</div>
        </div>
      ))}
      <div className="w-[110px] shrink-0 border-l-2 border-black flex flex-col items-center py-2 gap-2 bg-[#2d2f35]">
        <div className="text-[9px] font-mono text-zinc-400">MAIN OUT</div>
        <div className="w-[96px] rounded bg-black/30 p-1 text-[9px] font-mono text-zinc-500 text-center">Limiter · Comp</div>
        <div className="flex-1" />
        <div className="flex gap-2 items-end">
          <Fader value={song.masterVol} onChange={(v) => mutate((d) => { d.masterVol = v; })} height={150} testid="master-fader" />
          <Meter master height={150} width={8} />
        </div>
        <div className="text-[10px] font-mono text-zinc-300 bg-black/50 rounded px-1.5">{dbLabel(song.masterVol)}</div>
        <div className="w-[96px] h-6 rounded-sm flex items-center justify-center text-[11px] font-semibold bg-zinc-200 text-black">Main</div>
      </div>
    </div>
  );
}
